import moment from 'moment-timezone';
import { bucketWildcardTags, drawCategoryTypeIds } from './eventConstants';

export const TIME_FORMAT_12H = 'h:mm a';

export const countWords = (str) => {
    const trimmed = str?.trim();
    if (!trimmed) {
        return 0;
    }

    return trimmed.split(/\s+/).length;
};

/**
 * Format all parts of a name using TitleCase with the common conventions for
 * hyphens and other separators. Can't account for some suffixes like "III".
 * @param {string} value - A full name (multiple parts), or a single name part (first, last, middle, etc.)
 * @returns {string} The case-corrected name.
 */
export function titleCase(value) {
    const parts = value?.split(/\p{Z}/u);
    if (!parts) {
        return value;
    }
    return parts.map((namePart) => {
        const partSegments = namePart.split(/([\p{Pd}'])/u);
        return partSegments.map((segment, idx) => {
            // the captured separator
            if (idx % 2 === 1) {
                return segment;
            }
            // the separated value
            return segment?.charAt(0)?.toUpperCase() + segment?.substring(1).toLowerCase();
        }).join('');
    }).join(' ');
}

export function capitalize(string) {
    if (!string || typeof string !== 'string' || string.length === 0) {
        return '';
    }
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function getEventDateString(eventSchedule, showYear) {
    const { eventStartDate, eventEndDate, eventStartMonth, eventEndMonth, eventStartYear, eventEndYear } = eventSchedule;
    // A range that starts and ends in the same year states it once, after the end date - "Aug 19 - Sep
    // 22, 2026", not "Aug 19, 2026 - Sep 22, 2026". Only a range that genuinely straddles a year
    // boundary needs both, and then both are load-bearing: "Dec 28, 2026 - Jan 4, 2027".
    // The same-month branches below already read this way; this brings the cross-month one into line
    // (TMS-2013 feedback row 31).
    const spansYears = eventStartYear !== eventEndYear;
    const startYearString = showYear && spansYears ? `, ${eventStartYear}` : '';
    const endYearString = showYear ? `, ${eventEndYear}` : '';

    // `!spansYears` guards the collapse: "Aug" to "Aug" a year apart matches on month but must still
    // print both years, or a 12-month season reads as a three-day event.
    if (eventStartMonth === eventEndMonth && !spansYears) {
        if (eventStartDate === eventEndDate) {
            return `${eventStartMonth} ${eventStartDate}${endYearString}`;
        }
        return `${eventStartMonth} ${eventStartDate} - ${eventEndDate}${endYearString}`;
    }
    return `${eventStartMonth} ${eventStartDate}${startYearString} - ${eventEndMonth} ${eventEndDate}${endYearString}`;
}

export function getEventDateStringV2(eventSchedule, isPaidHitEvent = false) {
    const { eventStartUtc, timeZone, eventStartMonth, eventStartDate } = eventSchedule;
    const timeZoneValue = timeZone?.value || 'America/New_York';
    const today = moment().tz(timeZoneValue).startOf('day');
    const yesterday = moment().tz(timeZoneValue).subtract(1, 'days').startOf('day');
    const tomorrow = moment().tz(timeZoneValue).add(1, 'days').startOf('day');

    let dateString;
    if (eventStartUtc) {
        if (isPaidHitEvent) {
            dateString = moment.utc(eventStartUtc).tz(timeZoneValue).format('MMM D');
            const timeString = moment.utc(eventStartUtc).tz(timeZoneValue).format('h:mma z');
            return `${dateString} | ${timeString}`;
        }

        if (moment.utc(eventStartUtc).tz(timeZoneValue).isSame(today, 'd')) {
            dateString = 'TODAY';
        } else if (moment.utc(eventStartUtc).tz(timeZoneValue).isSame(yesterday, 'd')) {
            dateString = 'YESTERDAY';
        } else if (moment.utc(eventStartUtc).tz(timeZoneValue).isSame(tomorrow, 'd')) {
            dateString = 'TOMORROW';
        } else {
            dateString = moment.utc(eventStartUtc).tz(timeZoneValue).format('MMM D');
        }
        const timeString = moment.utc(eventStartUtc).tz(timeZoneValue).format('h:mma');
        return `${dateString} | ${timeString}`;
    }
    if (eventStartMonth && eventStartDate) {
        return `${eventStartMonth} ${eventStartDate}`;
    }
    return null;
}

export function getEventDates(eventSchedule) {
    const { eventStartDate, eventEndDate } = eventSchedule;
    if (eventStartDate === eventEndDate) {
        return eventStartDate;
    }
    return `${eventStartDate} - ${eventEndDate}`;
}

export function getEventMonths(eventSchedule) {
    const { eventStartMonth, eventEndMonth } = eventSchedule;
    if (eventStartMonth === eventEndMonth) {
        return eventStartMonth;
    }
    return `${eventStartMonth} - ${eventEndMonth}`;
}

export function formatEventDate(start, end, timeZoneValue) {
    const startDate = moment.utc(start).tz(timeZoneValue);
    const endDate = moment.utc(end).tz(timeZoneValue);

    return `${startDate?.format('MMM D')?.toUpperCase()} 
            ${startDate?.month() === endDate?.month() && startDate?.day() === endDate?.day()
            ? '' : ` - ${startDate?.month() === endDate?.month() ? endDate?.format('D') : endDate?.format('MMM D')?.toUpperCase()}`}`;
}

export function formatMatchDate(date, shortFormat) {
    // this does not handle timezone, currently used by player profile stats tab.
    if (date) {
        if (shortFormat) {
            return moment(date).format('MMM D');
        }
        return moment(date).format('ddd MMM D');
    }

    return '';
}

export function formatMatchDateWithOffset(date, timeZoneValue = 'UTC', shortFormat, addYear) {
    if (date) {
        if (addYear) {
            if (shortFormat) {
                return moment.utc(date).tz(timeZoneValue).format('MMM D YYYY');
            }
            return moment.utc(date).tz(timeZoneValue).format('ddd MMM D YYYY');
        }
        if (shortFormat) {
            return moment.utc(date).tz(timeZoneValue).format('MMM D');
        }
        return moment.utc(date).tz(timeZoneValue).format('ddd MMM D');
    }

    return '';
}

export function formatMatchDateForEventPage(date, timeZoneValue = 'UTC', shortFormat) {
    if (date && timeZoneValue) {
        if (shortFormat) {
            return moment.utc(date).tz(timeZoneValue).format('MMM D');
        }
        return moment.utc(date).tz(timeZoneValue).format('ddd MMM D');
    }

    return '';
}

export function formatMatchTime(date, timeZoneValue, shortFormat) {
    if (date) {
        const dateMoment = moment.utc(date).tz(timeZoneValue);
        if (shortFormat) {
            return dateMoment.format('h:mm');
        }
        return dateMoment.format('h:mma');
    }
    return '';
}

/**
 * Formats a lock end date for display (e.g. roster locked popup).
 * Uses UTC and "MMM D, YYYY" to match existing date display patterns.
 * @param {string|Date|null|undefined} date - ISO date string or Date
 * @returns {string} Formatted date or empty string if no date
 */
export function formatLockEndDate(date) {
    if (date) {
        const m = moment.utc(date);
        return m.isValid() ? m.format('MMM D, YYYY') : '';
    }
    return '';
}

export const formatToFixed = (value, decimals = 2) => {
    if (value == null) {
        return '0.00';
    }
    return value.toFixed(decimals);
};

export const truncateRating = (value, decimals = 2) => {
    if (!Number.isFinite(value)) {
        return '0.00';
    }
    const factor = 10 ** decimals;
    return (Math.trunc(Number((value * factor).toFixed(6))) / factor).toFixed(decimals);
};

/**
 * https://stackoverflow.com/questions/14446511/what-is-the-most-efficient-method-to-groupby-on-a-javascript-array-of-objects
 */
export function groupBy(list, keyGetter) {
    const map = new Map();
    list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
}

export function splitTitle(string) {
    const object = {};
    if (string) {
        const title = string.trim();
        if (countWords(title) > 1) {
            const index = title.lastIndexOf(' ');
            object.firstPart = title.substr(0, index + 1);
            object.lastPart = title.substr(index + 1);
        } else {
            object.firstPart = '';
            object.lastPart = title;
        }
    } else {
        object.firstPart = '';
        object.lastPart = '';
    }

    return object;
}

export function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatNumber(value) {
    if (value == null || value === '') { return '0'; }
    return numberWithCommas(Number(value));
}

export function nth(d) {
    if (d > 3) {
        return 'th';
    }

    switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
}

export const createTypeByIdDict = (typesArr) => {
    const dict = {};
    typesArr.forEach(type => {
        dict[type.id] = type;
    });
    return dict;
};

export const updateCategoryTypesName = (categoryTypeDict) => {
    const updatedValues = {
        ...categoryTypeDict,
    };

    const autoPlayOffType = categoryTypeDict[drawCategoryTypeIds.AUTO_PLAYOFF];
    if (autoPlayOffType) {
        updatedValues[autoPlayOffType.id] = {
            ...autoPlayOffType,
            label: 'Playoff',
        };
    }

    return updatedValues;
};

export const getPlayerCardPlayerDetails = (player, isCurrentMemberAdmin, showHighSchoolName, showAge = true) => {
    const { acceptanceListWildcardStateId, location, descriptionLong, gender, age } = player;
    const getDescription = () => {
        if (showHighSchoolName && player.highSchool) {
            return [player.highSchool];
        }
        if (location?.display) {
            return [location.display];
        }
    };

    let description = '';
    if (descriptionLong) {
        description = [descriptionLong];
    } else {
        description = getDescription() || [];
    }

    let showWildcardTag = isCurrentMemberAdmin && acceptanceListWildcardStateId && acceptanceListWildcardStateId > 1;
    if (acceptanceListWildcardStateId && acceptanceListWildcardStateId === 4) { // special exempt
        showWildcardTag = true;
    }

    return [
        ...(showWildcardTag ? [bucketWildcardTags[acceptanceListWildcardStateId]] : []),
        ...(isCurrentMemberAdmin && showAge ? [age] : []),
        ...(gender ? [gender.charAt(0)] : []),
        ...(description),
    ];
};

export function pluralize(singular, count) {
    if (count === 1) {
        return singular;
    }

    // Handle special cases
    const lastChar = singular.slice(-1);

    if (lastChar === 'y' && !'aeiou'.includes(singular.slice(-2, -1))) {
        return `${singular.slice(0, -1)}ies`;
    }

    if (['s', 'x', 'z', 'ch', 'sh'].some(ending => singular.endsWith(ending))) {
        return `${singular}es`;
    }

    return `${singular}s`;
}

export function formatDecimal(value, decimals = 2) {
    if (value == null || value === '') {
        return '';
    }
    const num = Number(value);
    return Number.isNaN(num) ? value : num.toFixed(decimals);
}
